import {
  Box,
  CircularProgress,
  FormControlLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
} from '@material-ui/core'
import { DatePicker } from '@material-ui/pickers'
import { Button } from 'components/facets'
import { TablePaginationControls } from 'components/facets/TablePaginationControls'
import { useUser } from 'components/providers/UserProvider'
import orderImg from 'img/order-2.png'
import {
  createOrderCsvReport,
  downloadOrderCsvReport,
  fetchOrderCsvReports,
  getStatusOrderCsvReport,
} from 'lib/api'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AutoSizer, Column, Table, TableRowRenderer } from 'react-virtualized'
import {
  ChannelFilterOptions,
  CsvReportHistory,
  formatDateToYYYYMMDD,
  getNDaysAgo,
  normalizeCsvReportHistory,
  parseLocalDate,
  SUPPORTED_CURRENCIES,
  toEndOfInterval,
} from 'shared'
import './exports-presenter.css'

export const OrdersExports = (): JSX.Element => {
  const { t } = useTranslation()
  const { user } = useUser()
  const createdAtRaw = (user as any).created_at ?? user.createdAt
  const rawCreatedAt = createdAtRaw ? new Date(createdAtRaw) : null
  const accountCreatedAt = rawCreatedAt && !isNaN(rawCreatedAt.getTime()) ? rawCreatedAt : null
  const defaultFrom = getNDaysAgo(30)
  const [from, setFrom] = useState<Date>(
    accountCreatedAt && defaultFrom < accountCreatedAt ? accountCreatedAt : defaultFrom
  )
  const [to, setTo] = useState<Date>(new Date())
  const [currency, setCurrency] = useState<string>('')
  const [salesChannel, setSalesChannel] = useState<string>('')
  const [exportType, setExportType] = useState<string>('orders')
  const { enqueueSnackbar } = useSnackbar()
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [isLastPage, setIsLastPage] = useState<boolean>(true)

  const [downloads, setDownloads] = useState<CsvReportHistory[]>([])
  const [loadingReports, setLoadingReports] = useState(true)
  const [filterMonth, setFilterMonth] = useState<Date | null>(null)
  const [hasRecords, setHasRecords] = useState(false)
  const [isCreating, setIsCreating] = useState(false)

  const today = new Date()
  const todayStr = formatDateToYYYYMMDD(today)
  const minDateStr = accountCreatedAt ? formatDateToYYYYMMDD(accountCreatedAt) : undefined

  const handleCreateCsv = async () => {
    if (from > today || to > today) {
      enqueueSnackbar(t('exports.errors.errorFutureDate'), { variant: 'error' })
      return
    }
    setIsCreating(true)
    try {
      const exportJob = await createOrderCsvReport(
        from.toISOString(),
        toEndOfInterval(to).toISOString(),
        currency || undefined,
        salesChannel || undefined,
        exportType
      )
      const normalize = normalizeCsvReportHistory(exportJob)

      pollExportStatus(normalize.id)

      setDownloads((prev) => [normalize, ...prev])
      setHasRecords(true)
    } catch {
      enqueueSnackbar(t('exports.errors.errorCreateExport'), { variant: 'error' })
    } finally {
      setIsCreating(false)
    }
  }

  const handleFetchReports = async (overrideMonth?: Date | null) => {
    setLoadingReports(true)
    const month = overrideMonth !== undefined ? overrideMonth : filterMonth
    try {
      let createdAtFrom: string | undefined
      let createdAtTo: string | undefined
      if (month) {
        const year = month.getFullYear()
        const m = month.getMonth() + 1
        const monthStr = `${year}-${String(m).padStart(2, '0')}`
        createdAtFrom = `${monthStr}-01`
        const lastDay = new Date(year, m, 0).getDate()
        createdAtTo = `${monthStr}-${String(lastDay).padStart(2, '0')}`
      }
      const { files, hasNextPage } = await fetchOrderCsvReports(
        currentPage,
        createdAtFrom,
        createdAtTo
      )
      if (files) {
        setDownloads(files)
        setIsLastPage(!hasNextPage)
        if (!month && files.length > 0) setHasRecords(true)
      } else {
        enqueueSnackbar(t('exports.errors.errorFetchReports'), { variant: 'error' })
      }
    } finally {
      setLoadingReports(false)
    }
  }

  const pollExportStatus = async (id: number) => {
    let res
    try {
      res = await getStatusOrderCsvReport(id)
    } catch {
      // without this catch, a rejected axios request bubbles up and the
      // polling silently stops, leaving the UI stuck on "pending" with
      // no feedback to the user.
      enqueueSnackbar(t('exports.errors.errorCheckExportStatus'), { variant: 'error' })
      return
    }

    if (res.status === 'ready') {
      setDownloads((prev) =>
        prev.map((exp) => (exp.id === id ? { ...exp, exportEndedAt: new Date() } : exp))
      )
      enqueueSnackbar(t('exports.csvReady'), { variant: 'success' })
      handleFetchReports()
      return
    }

    if (res.status === 'pending') {
      setTimeout(() => pollExportStatus(id), 5000)
      return
    }

    enqueueSnackbar(t('exports.errors.errorCheckExportStatus'), { variant: 'error' })
  }

  useEffect(() => {
    handleFetchReports()
  }, [currentPage])

  const handleApplyFilters = () => {
    if (currentPage === 1) {
      handleFetchReports()
    } else {
      setCurrentPage(1)
    }
  }

  const handleResetFilter = () => {
    setFilterMonth(null)
    if (currentPage === 1) {
      handleFetchReports(null)
    } else {
      setCurrentPage(1)
    }
  }

  const handleDownloadCsv = async (id: number, exportType?: string) => {
    let res
    try {
      res = await downloadOrderCsvReport(id)
    } catch {
      // the request uses responseType: 'blob', so axios error interceptors
      // that try to read responseText as text throw InvalidStateError. Also
      // we need explicit feedback instead of letting the failure bubble up.
      enqueueSnackbar(t('exports.errors.errorDownloadCsv'), { variant: 'error' })
      return
    }

    const typeSuffix = exportType === 'order_articles' ? '-articles' : '-orders'

    const blob = new Blob([res.data], { type: 'text/csv' })

    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `orders-report${typeSuffix}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }

  const rowRenderer: TableRowRenderer = ({ key, style, columns, className, rowData }) => {
    rowData

    return (
      <div key={key} style={style} className={`${className}`}>
        {columns}
      </div>
    )
  }

  const channelOptions = [
    <MenuItem key="all" value="">
      {t('exports.allChannels')}
    </MenuItem>,
    ...Object.values(ChannelFilterOptions)
      .slice(1)
      .map((channel) => (
        <MenuItem key={channel} value={channel}>
          {t(`common.channels.${channel}`)}
        </MenuItem>
      )),
  ]

  return (
    <div className="tab-page-content">
      {loadingReports ? (
        <div className="w-100 d-flex flex-column align-items-center mt-5">
          <CircularProgress size={60} />
        </div>
      ) : !hasRecords ? (
        <div className="w-100 d-flex flex-column align-items-center mt-3">
          <img src={orderImg} style={{ maxWidth: 600, width: '100%', height: 'auto' }} />
          <Box maxWidth={800} width="100%" px={2} className="text-center">
            <h2 className="fst-italic mt-3 text-center">{t('exports.ordersDescription')}</h2>
            <Grid
              container
              spacing={2}
              alignItems="center"
              justifyContent="center"
              className="mt-3"
            >
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  label={t('orders.from')}
                  type="date"
                  defaultValue={formatDateToYYYYMMDD(from)}
                  onChange={(e) => setFrom(parseLocalDate(e.target.value))}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: minDateStr, max: todayStr }}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  label={t('orders.to')}
                  type="date"
                  defaultValue={formatDateToYYYYMMDD(to)}
                  onChange={(e) => setTo(parseLocalDate(e.target.value))}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: minDateStr, max: todayStr }}
                />
              </Grid>
              <Grid item xs={6} sm={3} className="mt-3">
                <Select
                  fullWidth
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value as string)}
                  displayEmpty
                >
                  <MenuItem value="">{t('exports.allCurrencies')}</MenuItem>
                  {SUPPORTED_CURRENCIES.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={6} sm={3} className="mt-3">
                <Select
                  fullWidth
                  value={salesChannel}
                  onChange={(e) => setSalesChannel(e.target.value as string)}
                  displayEmpty
                >
                  {channelOptions}
                </Select>
              </Grid>
              <Grid item xs={12} className="d-flex align-items-center justify-content-center">
                <RadioGroup row value={exportType} onChange={(e) => setExportType(e.target.value)}>
                  <FormControlLabel
                    value="orders"
                    control={<Radio color="primary" />}
                    label={t('exports.exportTypeOrders')}
                  />
                  <FormControlLabel
                    value="order_articles"
                    control={<Radio color="primary" />}
                    label={t('exports.exportTypeOrderArticles')}
                  />
                </RadioGroup>
                <Button
                  variant="contained"
                  onClick={handleCreateCsv}
                  className="ms-3"
                  disabled={isCreating}
                >
                  {isCreating ? <CircularProgress size={20} /> : t('csv.export')}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </div>
      ) : (
        <>
          <div>
            <h2 className="mb-4">{t('exports.ordersTitle')}</h2>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={6} sm={3} md={2}>
                <TextField
                  fullWidth
                  label={t('orders.from')}
                  type="date"
                  defaultValue={formatDateToYYYYMMDD(from)}
                  onChange={(e) => setFrom(parseLocalDate(e.target.value))}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: minDateStr, max: todayStr }}
                />
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <TextField
                  fullWidth
                  label={t('orders.to')}
                  type="date"
                  defaultValue={formatDateToYYYYMMDD(to)}
                  onChange={(e) => setTo(parseLocalDate(e.target.value))}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: minDateStr, max: todayStr }}
                />
              </Grid>
              <Grid item xs={6} sm={3} md={2} className="mt-3">
                <Select
                  fullWidth
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value as string)}
                  displayEmpty
                >
                  <MenuItem value="">{t('exports.allCurrencies')}</MenuItem>
                  {SUPPORTED_CURRENCIES.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={6} sm={3} md={2} className="mt-3">
                <Select
                  fullWidth
                  value={salesChannel}
                  onChange={(e) => setSalesChannel(e.target.value as string)}
                  displayEmpty
                >
                  {channelOptions}
                </Select>
              </Grid>
              <Grid item xs={12} className="d-flex align-items-center">
                <RadioGroup row value={exportType} onChange={(e) => setExportType(e.target.value)}>
                  <FormControlLabel
                    value="orders"
                    control={<Radio color="primary" />}
                    label={t('exports.exportTypeOrders')}
                  />
                  <FormControlLabel
                    value="order_articles"
                    control={<Radio color="primary" />}
                    label={t('exports.exportTypeOrderArticles')}
                  />
                </RadioGroup>
                <Button
                  variant="contained"
                  onClick={handleCreateCsv}
                  className="ms-3"
                  disabled={isCreating}
                >
                  {isCreating ? <CircularProgress size={20} /> : t('csv.export')}
                </Button>
              </Grid>
            </Grid>
            {accountCreatedAt && (
              <small className="text-muted">
                {t('exports.accountCreatedOn', {
                  date: accountCreatedAt.toLocaleDateString(),
                })}
              </small>
            )}
          </div>

          <div className="d-flex align-items-center gap-2 flex-wrap mt-4 mb-3">
            <span>{t('exports.showExportsCovering')}</span>
            <DatePicker
              views={['year', 'month']}
              openTo="month"
              format="MM/yyyy"
              variant="inline"
              autoOk
              className="muiDatePicker"
              value={filterMonth}
              onChange={(date) => setFilterMonth(date)}
              maxDate={today}
              inputProps={{ style: { padding: '4px 8px', fontSize: 13 } }}
              style={{ width: 170 }}
              PopoverProps={{ classes: { paper: 'exports-month-picker' } }}
            />
            <Button variant="outlined" onClick={handleApplyFilters}>
              {t('exports.applyFilter')}
            </Button>
            {filterMonth && (
              <Button variant="outlined" onClick={handleResetFilter}>
                {t('exports.resetFilter')}
              </Button>
            )}
          </div>

          <div>
            <div className="responsive-table-container">
              <AutoSizer disableHeight>
                {({ width: autoWidth }) => {
                  const width = Math.max(autoWidth, 900)
                  return (
                    <Table
                      width={width}
                      height={40 + Math.max(40, downloads.length * 40)}
                      headerHeight={40}
                      rowHeight={40}
                      rowCount={downloads.length}
                      rowGetter={({ index }) => downloads[index]}
                      rowRenderer={rowRenderer}
                      overscanRowCount={5}
                    >
                      <Column
                        width={width * 0.15}
                        label={t('exports.fromDate')}
                        dataKey="from"
                        cellRenderer={({ rowData: a }) => (
                          <div className="text-wrap">
                            {new Date(a.intervalStartDate).toLocaleString()}
                          </div>
                        )}
                      />
                      <Column
                        width={width * 0.15}
                        label={t('exports.toDate')}
                        dataKey="to"
                        cellRenderer={({ rowData: a }) => (
                          <div className="text-wrap">
                            {new Date(a.intervalEndDate).toLocaleString()}
                          </div>
                        )}
                      />
                      <Column
                        width={width * 0.15}
                        label={t('exports.createdAt')}
                        dataKey="created"
                        cellRenderer={({ rowData: a }) => (
                          <div className="text-wrap">{new Date(a.createdAt).toLocaleString()}</div>
                        )}
                      />
                      <Column
                        width={width * 0.12}
                        label={t('exports.exportType')}
                        dataKey="exportType"
                        cellRenderer={({ rowData: a }) => {
                          const type = a.params?.exportType
                          if (!type) return <div className="text-wrap">—</div>
                          return (
                            <div className="text-wrap">
                              {type === 'order_articles'
                                ? t('exports.exportTypeOrderArticles')
                                : t('exports.exportTypeOrders')}
                            </div>
                          )
                        }}
                      />
                      <Column
                        width={width * 0.12}
                        label={t('exports.currency')}
                        dataKey="currency"
                        cellRenderer={({ rowData: a }) => (
                          <div className="text-wrap">
                            {a.params?.currency || t('exports.allCurrencies')}
                          </div>
                        )}
                      />
                      <Column
                        width={width * 0.15}
                        label={t('exports.salesChannel')}
                        dataKey="salesChannel"
                        cellRenderer={({ rowData: a }) => (
                          <div className="text-wrap">
                            {a.params?.salesChannel || t('exports.allChannels')}
                          </div>
                        )}
                      />
                      <Column
                        width={width * 0.08}
                        label={t('exports.status')}
                        dataKey="status"
                        headerClassName="centered-header"
                        cellRenderer={({ rowData: a }) => (
                          <Box textAlign={'center'}>
                            {a.lastErrorAt ? (
                              <i className="fa-light fa-circle-x error-color" />
                            ) : a.exportEndedAt ? (
                              <i className="fa-light fa-circle-check" style={{ color: 'green' }} />
                            ) : (
                              <CircularProgress size={16} />
                            )}
                          </Box>
                        )}
                      />
                      <Column
                        width={width * 0.08}
                        label={t('exports.download')}
                        dataKey="download"
                        headerClassName="centered-header"
                        cellRenderer={({ rowData: a }) => (
                          <Box textAlign={'center'}>
                            {!a.lastErrorAt && a.exportEndedAt ? (
                              <i
                                className="fa-light fa-arrow-down-to-bracket table-action-icon"
                                onClick={() => handleDownloadCsv(a.id, a.params?.exportType)}
                                style={{ cursor: 'pointer' }}
                              />
                            ) : (
                              '—'
                            )}
                          </Box>
                        )}
                      />
                    </Table>
                  )
                }}
              </AutoSizer>
            </div>
            {(currentPage > 1 || !isLastPage) && (
              <TablePaginationControls
                currentPage={currentPage}
                setPage={setCurrentPage}
                isLastPage={isLastPage}
                startFromZero={false}
              />
            )}
          </div>
        </>
      )}
    </div>
  )
}
